import _ from "lodash";
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import { ATTACHMENT_TYPES, CHAT_SCREEN_MODE } from "Utils/MessageHelpers";
import { useMessageOptions } from "UIComponents/Message/providers/MessageProvider";

import MediaWrapper, { MEDIA_VISIBILITY } from "../MediaWrapper";
import classes from "./GridVisualMediaContainer.scss";
import {
  MEDIA_DIMENSIONS_PER_VIEW_MODE,
  SQUARE_THUMBNAIL_RESOLUTION,
} from "../Utils";

const CountIcon = ({ moreCount }) => {
  return (
    <div className={classNames("text-textOn", "text-label-1", classes.count)}>
      {`+ ${moreCount}`}
    </div>
  );
};

const VISUAL_GRID_SIZE = 4;

const GridVisualMediaContainer = props => {
  const { attachments, onMediaClick } = props;
  const {
    screenMode = CHAT_SCREEN_MODE.VIEW_MODE_SCREEN,
  } = useMessageOptions();

  const gridItems = useMemo(() => {
    // we only want to show the first 4 attachments in grid view
    // remaining attachments would be visible with TheatreMode
    const firstFour = _.filter(
      attachments,
      (_attachment, idx) => idx < VISUAL_GRID_SIZE
    );

    // this will give us an array of 2 objects having two attachments each.
    return _.reduce(
      firstFour,
      (result, attachment, idx) => {
        const arrIdxToPushTo = idx < 2 ? 0 : 1;
        result[arrIdxToPushTo].attachments.push(attachment);
        return result;
      },
      [
        { id: 1, attachments: [] },
        { id: 2, attachments: [] },
      ]
    );
  }, [attachments]);

  const dimensions = {
    height: MEDIA_DIMENSIONS_PER_VIEW_MODE[screenMode].height,
    width: MEDIA_DIMENSIONS_PER_VIEW_MODE[screenMode].width,
  };

  return (
    <div className={classes.container}>
      {_.map(gridItems, (grid, rowIdx) => (
        <div className={classes.row} key={grid.id}>
          {_.map(grid.attachments, (attachment, colIdx) => {
            const { id, type } = attachment;
            const isVideo = type === ATTACHMENT_TYPES.VIDEO;

            const isLastGridItem = rowIdx === 1 && colIdx === 1;
            const remainingAttachmentsCount =
              _.size(attachments) - VISUAL_GRID_SIZE;
            const hasRemainingAttachmentsOnLastGrid =
              isLastGridItem && remainingAttachmentsCount > 0;

            return (
              <MediaWrapper
                {...dimensions}
                onMediaClick={onMediaClick}
                key={id}
                attachment={attachment}
                imgClassName={classes.image}
                thumbnailResolution={SQUARE_THUMBNAIL_RESOLUTION}
                overlayVisibility={
                  hasRemainingAttachmentsOnLastGrid
                    ? MEDIA_VISIBILITY.PERSISTENT
                    : undefined
                }
                iconVisibility={
                  isVideo || hasRemainingAttachmentsOnLastGrid
                    ? MEDIA_VISIBILITY.PERSISTENT
                    : undefined
                }
                customIcon={
                  hasRemainingAttachmentsOnLastGrid ? (
                    <CountIcon moreCount={remainingAttachmentsCount} />
                  ) : undefined
                }
              />
            );
          })}
        </div>
      ))}
    </div>
  );
};

GridVisualMediaContainer.propTypes = {
  onMediaClick: PropTypes.func.isRequired,
  attachments: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default GridVisualMediaContainer;
